import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserContextData, AIProfile } from '../services/ai.service.js';

export interface ChatAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  size: number;
  base64Data: string;
  thumbnailUrl?: string;
}

export interface AIChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  toolName?: string;
  toolResult?: unknown;
  toolCalls?: Array<{ name: string; args: Record<string, unknown>; result: unknown }>;
  timestamp: string;
  /** Image attachments sent with the message */
  attachments?: ChatAttachment[];
  /** Model thinking/reasoning content (from <think> blocks) */
  thinking?: string;
  /** Token usage info */
  usage?: { promptTokens: number; completionTokens: number; totalTokens: number };
  /** Debug info (request/response/timing) */
  debugInfo?: Record<string, unknown>;
  /** Context window info (from server) */
  contextInfo?: { totalMessages: number; loadedMessages: number; tokenBudget: number; tokenUsed: number; summaryCount: number; tokensSaved?: number };
  /** Prompt cache metrics (Anthropic SDK) */
  cacheMetrics?: { cacheCreationTokens: number; cacheReadTokens: number };
}

export interface AIConversationSummary {
  id: number;
  title: string;
  status: string;
  messageCount: number;
  updatedAt: string;
}

interface AIState {
  // Chat widget state
  chatOpen: boolean;
  chatPosition: { x: number; y: number };
  chatSize: { width: number; height: number };
  chatMinimized: boolean;

  // Current conversation
  activeConversationId: number | null;
  messages: AIChatMessage[];
  conversations: AIConversationSummary[];
  isTyping: boolean;

  // Connection status
  connected: boolean;

  // Full page mode
  fullPageMode: boolean;

  // User context (loaded from server)
  userContext: UserContextData | null;
  userContextLoading: boolean;

  // Continuation
  continuationPending: boolean;
  continuationTimeout: number;
  continuationCount: number;

  // Profile management
  activeProfileId: number | null;
  profiles: AIProfile[];

  // Streaming state
  streamingMessageId: string | null;

  // Message editing state
  editingMessageId: string | null;
  editingContent: string;

  // Stop generation state
  isStopping: boolean;

  // Destructive tool confirmation (roadmap #48 PR-B)
  pendingTool: {
    correlationId: string;
    name: string;
    args: Record<string, unknown>;
    dangerLevel: 'destructive';
    timeoutMs: number;
    receivedAt: number;
  } | null;

  // Prefill: allows other pages to queue text to appear in the chat input when opened
  pendingInput: string | null;
  setPendingInput: (input: string | null) => void;

  // Actions — chat widget
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  setChatPosition: (pos: { x: number; y: number }) => void;
  setChatSize: (size: { width: number; height: number }) => void;
  toggleMinimize: () => void;

  // Actions — messages
  addMessage: (msg: AIChatMessage) => void;
  setMessages: (msgs: AIChatMessage[]) => void;
  clearMessages: () => void;
  setIsTyping: (v: boolean) => void;

  // Actions — conversations
  setActiveConversation: (id: number | null) => void;
  setConversations: (convs: AIConversationSummary[]) => void;

  // Actions — connection
  setConnected: (v: boolean) => void;

  // Actions — full page
  setFullPageMode: (v: boolean) => void;

  // Actions — user context
  setUserContext: (ctx: UserContextData | null) => void;
  setUserContextLoading: (v: boolean) => void;

  // Actions — continuation
  setContinuationPending: (v: boolean) => void;
  setContinuationTimeout: (v: number) => void;
  incrementContinuationCount: () => void;
  resetContinuationCount: () => void;

  // Actions — profiles
  setActiveProfile: (id: number | null) => void;
  setProfiles: (profiles: AIProfile[]) => void;

  // Actions — streaming
  setStreamingMessageId: (id: string | null) => void;
  appendToMessage: (id: string, field: 'content' | 'thinking', text: string) => void;
  updateMessage: (id: string, updates: Partial<AIChatMessage>) => void;

  // Actions — message editing
  setEditingMessage: (id: string | null, content: string) => void;
  setEditingContent: (content: string) => void;

  // Actions — stop generation
  setIsStopping: (v: boolean) => void;

  // Actions — destructive tool confirmation
  setPendingTool: (p: AIState['pendingTool']) => void;
  clearPendingTool: () => void;
}

export const useAIStore = create<AIState>()(
  persist(
    (set) => ({
      chatOpen: false,
      chatPosition: { x: -1, y: -1 }, // -1 = use default (bottom-right)
      chatSize: { width: 400, height: 520 },
      chatMinimized: false,
      activeConversationId: null,
      messages: [],
      conversations: [],
      isTyping: false,
      connected: false,
      fullPageMode: false,
      userContext: null,
      userContextLoading: false,
      continuationPending: false,
      continuationTimeout: 30000,
      continuationCount: 0,
      activeProfileId: null,
      profiles: [],
      streamingMessageId: null,
      editingMessageId: null,
      editingContent: '',
      isStopping: false,
      pendingTool: null,
      pendingInput: null,
      setPendingInput: (input) => set({ pendingInput: input }),
      setPendingTool: (p) => set({ pendingTool: p }),
      clearPendingTool: () => set({ pendingTool: null }),

      toggleChat: () => set((s) => ({ chatOpen: !s.chatOpen, chatMinimized: false })),
      openChat: () => set({ chatOpen: true, chatMinimized: false }),
      closeChat: () => set({ chatOpen: false }),
      setChatPosition: (pos) => set({ chatPosition: pos }),
      setChatSize: (size) => set({ chatSize: size }),
      toggleMinimize: () => set((s) => ({ chatMinimized: !s.chatMinimized })),

      addMessage: (msg) => set((s) => ({ messages: [...s.messages, msg] })),
      setMessages: (msgs) => set({ messages: msgs }),
      clearMessages: () => set({ messages: [] }),
      setIsTyping: (v) => set({ isTyping: v }),

      setActiveConversation: (id) => set({ activeConversationId: id }),
      setConversations: (convs) => set({ conversations: convs }),

      setConnected: (v) => set({ connected: v }),
      setFullPageMode: (v) => set({ fullPageMode: v }),
      setUserContext: (ctx) => set({ userContext: ctx }),
      setUserContextLoading: (v) => set({ userContextLoading: v }),
      setContinuationPending: (v) => set({ continuationPending: v }),
      setContinuationTimeout: (v) => set({ continuationTimeout: v }),
      incrementContinuationCount: () => set((s) => ({ continuationCount: s.continuationCount + 1 })),
      resetContinuationCount: () => set({ continuationCount: 0 }),
      setActiveProfile: (id) => set({ activeProfileId: id }),
      setProfiles: (profiles) => set({ profiles }),
      setStreamingMessageId: (id) => set({ streamingMessageId: id }),

      setEditingMessage: (id, content) => set({
        editingMessageId: id ?? null,
        editingContent: content ?? '',
      }),
      setEditingContent: (content) => set({ editingContent: content }),
      setIsStopping: (v) => set({ isStopping: v }),
      appendToMessage: (id, field, text) =>
        set((s) => ({
          messages: s.messages.map((m) =>
            m.id === id ? { ...m, [field]: (m[field] || '') + text } : m,
          ),
        })),
      updateMessage: (id, updates) =>
        set((s) => ({
          messages: s.messages.map((m) =>
            m.id === id ? { ...m, ...updates } : m,
          ),
        })),
    }),
    {
      name: 'elmes-ai',
      partialize: (state) => ({
        chatPosition: state.chatPosition,
        chatSize: state.chatSize,
        activeProfileId: state.activeProfileId,
      }),
    },
  ),
);
